/*
  La Matcha — "add your last order again". Markup: snippets/reorder-card.liquid.

    <reorder-notice>   card under the header (sections/reorder-notice.liquid)
    <reorder-block>    card in the empty cart drawer and on the empty cart page

  Children of both: [data-reorder-card], [data-reorder-add], [data-reorder-error],
  <script type="application/json" data-reorder-items> with [{ id, quantity }].

  Sample order (Theme settings > Cart > Sample order). The elements carry data-sample when they
  show it. With "Only while the store is password protected" the card arrives inside
  <template data-reorder-sample> (a block is [hidden] as well) and is put in the page once
  storefrontLocked() says yes. The notice in the theme editor is rendered directly as its
  preview; there only the sample note ([data-reorder-note-auto]) waits for that answer.

  After a reorder from any of them the notice is remembered as handled and the blocks stay hidden
  until the next page load, so emptying the cart does not bring the offer straight back.

  Every section that renders the card loads this file; it runs once.
*/
(() => {
  const LM = (window.LM = window.LM || {});
  if (LM.reorderLoaded) return;
  LM.reorderLoaded = true;

  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const cartError = () => (LM.strings && LM.strings.cartError) || '';

  const stored = (area, key) => {
    try {
      return window[area].getItem(key);
    } catch (error) {
      return null;
    }
  };

  const store = (area, key, value) => {
    try {
      window[area].setItem(key, value);
    } catch (error) {
      /* Storage is unavailable (private mode): nothing is remembered. */
    }
  };

  /* ---------- Is the storefront behind the password page? ----------
     Liquid cannot tell, so the password URL is asked, once per browser session: a protected
     storefront answers it with the password page (200), an open one redirects it to the home
     page. Everything else (offline, blocked, an unexpected status) counts as open, so sample
     content can only appear on a storefront that is known to be locked. */
  const LOCK_KEY = 'lm:storefront-locked';
  let lockCheck = null;

  const storefrontLocked = () => {
    if (lockCheck) return lockCheck;
    const known = stored('sessionStorage', LOCK_KEY);
    if (known === '1' || known === '0') {
      lockCheck = Promise.resolve(known === '1');
      return lockCheck;
    }
    const root = ((LM.routes && LM.routes.root) || '/').replace(/\/$/, '');
    lockCheck = fetch(`${root}/password`, {
      method: 'HEAD',
      redirect: 'manual',
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then((response) => {
        const locked = response.type === 'basic' && response.status === 200;
        const open =
          response.type === 'opaqueredirect' ||
          (response.status >= 300 && response.status < 500 && response.status !== 429);
        // A server error or a rate limit says nothing about the storefront: ask again next time.
        if (locked || open) store('sessionStorage', LOCK_KEY, locked ? '1' : '0');
        return locked;
      })
      .catch(() => false);
    return lockCheck;
  };

  /* Puts a waiting sample card in place when the storefront is locked.
     Resolves to whether the element has a card to work with. */
  const settle = (host) => {
    if (host.reorderSettled) return host.reorderSettled;
    const template = host.querySelector(':scope > template[data-reorder-sample]');
    if (!template) {
      const note = host.querySelector('[data-reorder-note-auto]');
      if (note) {
        storefrontLocked().then((locked) => {
          note.hidden = !locked;
        });
      }
      host.reorderSettled = Promise.resolve(!!host.querySelector('[data-reorder-card]'));
      return host.reorderSettled;
    }
    host.reorderSettled = storefrontLocked().then((locked) => {
      if (!locked) return false;
      template.replaceWith(template.content);
      return true;
    });
    return host.reorderSettled;
  };

  /* ---------- Memory of the notice ----------
     Stored: when the notice was closed or the order was added. The sample order has its own
     entry, so trying it out does not silence the notice for a real order later. */
  const NOTICE_KEY = 'lm:reorder-notice';
  const noticeKey = (sample) => (sample ? `${NOTICE_KEY}:sample` : NOTICE_KEY);

  const remember = (sample) => {
    if (!designMode) store('localStorage', noticeKey(sample), String(Date.now()));
  };

  const remembered = (sample, days) => {
    const since = Number(stored('localStorage', noticeKey(sample)) || 0);
    return since > 0 && Date.now() - since < days * 24 * 60 * 60 * 1000;
  };

  /* ---------- Adding the order ---------- */
  let reordered = false;

  const itemsOf = (host) => {
    const source = host.querySelector('[data-reorder-items]');
    try {
      const items = JSON.parse(source ? source.textContent : '[]');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  };

  const addItems = (items, opener) => {
    if (LM.cart && typeof LM.cart.add === 'function') {
      return Promise.resolve(LM.cart.add(items, { open: true, opener }));
    }
    // cart.js is not available: add through the Cart API.
    const routes = LM.routes || {};
    return fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    }).then(
      (response) => {
        if (!response.ok) throw new Error(cartError());
      },
      () => {
        throw new Error(cartError());
      }
    );
  };

  const finish = (host, hadFocus) => {
    reordered = true;
    remember(host.hasAttribute('data-sample'));
    document.querySelectorAll('reorder-notice').forEach((notice) => {
      if (notice.hide) notice.hide();
    });
    document.querySelectorAll('reorder-block').forEach((block) => {
      block.hidden = true;
    });

    // A block in the drawer hides together with the button that was pressed: hand focus to the
    // cart. (On the cart page the section is rendered again and cart.js moves focus itself.)
    const section = host.closest('[data-cart-section]');
    const lost = document.activeElement === document.body || host.contains(document.activeElement);
    const target = hadFocus && lost && section ? section.querySelector('[data-cart-focus]') : null;
    if (target) target.focus({ preventScroll: true });

    // No cart on this page to show the result in (cart type "page"): continue on the cart page.
    if (!designMode && !document.querySelector('[data-cart-section]')) {
      window.location.assign((LM.routes && LM.routes.cart) || '/cart');
    }
  };

  /* host may offer hold(reason) / release(reason) to pause its own timers while the request runs. */
  const reorder = (host, button, opener) => {
    const items = itemsOf(host);
    if (!items.length || button.classList.contains('is-loading')) return;
    const message = host.querySelector('[data-reorder-error]');
    if (message) message.hidden = true;
    const hadFocus = host.contains(document.activeElement);
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    if (host.hold) host.hold('adding');

    const settled = () => {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      if (host.release) host.release('adding');
    };

    addItems(items, opener).then(
      () => {
        settled();
        finish(host, hadFocus);
      },
      // Nothing was added (out of stock, connection lost): say why and keep the card.
      (error) => {
        settled();
        const text = (error && error.message) || cartError();
        if (message && text) {
          message.textContent = text;
          message.hidden = false;
        }
      }
    );
  };

  /* ---------- <reorder-notice> ----------
     Extra children: [data-reorder-close]. data-days: how long closing it or reordering is
     remembered (localStorage).
     Appears 1200ms after load, stays for 12s (the countdown pauses on hover and focus) and stays
     under the header while the page scrolls. In the theme editor it shows while its section is
     selected, without timers or storage. */
  let selectedSection = null;

  class ReorderNotice extends HTMLElement {
    get card() {
      return this.querySelector('[data-reorder-card]');
    }

    connectedCallback() {
      this.duration = 12000;
      this.remaining = this.duration;
      this.days = parseInt(this.dataset.days, 10) || 7;
      this.preview = designMode || this.hasAttribute('data-preview');
      this.sample = this.hasAttribute('data-sample');
      this.holds = new Set();

      if (!this.bound) {
        this.bound = true;
        this.onMove = () => {
          if (this.frame) return;
          this.frame = window.requestAnimationFrame(() => {
            this.frame = null;
            this.place();
          });
        };
        this.addEventListener('click', (event) => {
          const add = event.target.closest('[data-reorder-add]');
          // The notice disappears once the items are in the cart, so the cart button takes its
          // place as the element focus returns to when the drawer closes.
          if (add) reorder(this, add, document.querySelector('[data-cart-link]') || undefined);
          else if (event.target.closest('[data-reorder-close]')) this.dismiss();
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.shown()) this.dismiss();
        });
        this.addEventListener('pointerenter', () => this.hold('hover'));
        this.addEventListener('pointerleave', () => this.release('hover'));
        this.addEventListener('focusin', () => this.hold('focus'));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.release('focus');
        });
      }

      if (this.preview) {
        settle(this);
        if (selectedSection && this.closest(`[id="shopify-section-${selectedSection}"]`)) this.show();
        return;
      }
      if (reordered || remembered(this.sample, this.days)) return;
      const ready = settle(this);
      this.showTimer = window.setTimeout(() => {
        ready.then((has) => {
          if (has && this.isConnected && !reordered) this.show();
        });
      }, 1200);
    }

    disconnectedCallback() {
      window.clearTimeout(this.showTimer);
      this.hide();
    }

    shown() {
      const card = this.card;
      return !!card && !card.hidden;
    }

    show() {
      const card = this.card;
      if (!card || !card.hidden) return;
      card.hidden = false;
      this.place();
      window.addEventListener('scroll', this.onMove, { passive: true });
      window.addEventListener('resize', this.onMove);
      if (this.preview) return;
      this.remaining = this.duration;
      this.run();
    }

    hide() {
      const card = this.card;
      if (!card) return;
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
      card.hidden = true;
      this.classList.remove('is-paused');
      window.removeEventListener('scroll', this.onMove);
      window.removeEventListener('resize', this.onMove);
    }

    /* Closed by the customer: remembered for data-days. */
    dismiss() {
      if (!this.preview) remember(this.sample);
      this.hide();
    }

    run() {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
      if (this.preview || !this.shown()) return;
      if (this.holds.size) {
        this.classList.add('is-paused');
        return;
      }
      this.classList.remove('is-paused');
      this.startedAt = performance.now();
      this.hideTimer = window.setTimeout(() => this.hide(), Math.max(0, this.remaining));
    }

    hold(reason) {
      if (this.hideTimer) this.remaining -= performance.now() - this.startedAt;
      this.holds.add(reason);
      this.run();
    }

    release(reason) {
      this.holds.delete(reason);
      this.run();
    }

    /* Sits 10px under the header and points the arrow at the cart button. */
    place() {
      const card = this.card;
      if (!card) return;
      const header = document.querySelector('[data-site-header]');
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      this.style.setProperty('--reorder-top', `${Math.round(Math.max(0, bottom)) + 10}px`);
      const cart = document.querySelector('[data-cart-link]');
      const detached = !header || bottom <= 0 || !cart || !cart.getClientRects().length;
      this.classList.toggle('is-detached', detached);
      if (detached) return;
      const cartBox = cart.getBoundingClientRect();
      const cardBox = card.getBoundingClientRect();
      const offset = cardBox.right - 2 - (cartBox.left + cartBox.width / 2) - 7;
      const max = cardBox.width - 30;
      this.style.setProperty('--reorder-arrow', `${Math.round(Math.min(Math.max(offset, 12), max))}px`);
    }
  }

  /* ---------- <reorder-block> ----------
     Lives where the empty cart is shown; the cart markup hides it while the cart has items. */
  class ReorderBlock extends HTMLElement {
    connectedCallback() {
      // An empty cart that is rendered again after a reorder brings a new block with it.
      if (reordered) {
        this.hidden = true;
        return;
      }
      if (!this.bound) {
        this.bound = true;
        this.addEventListener('click', (event) => {
          const add = event.target.closest('[data-reorder-add]');
          if (add) reorder(this, add);
        });
      }
      const waiting = this.hidden;
      settle(this).then((has) => {
        if (has && waiting && !reordered) this.hidden = false;
      });
    }
  }

  if (!customElements.get('reorder-notice')) customElements.define('reorder-notice', ReorderNotice);
  if (!customElements.get('reorder-block')) customElements.define('reorder-block', ReorderBlock);

  /* ---------- Theme editor: show the notice while its section is selected ---------- */
  if (designMode) {
    const noticeIn = (sectionId) => {
      const section = document.getElementById(`shopify-section-${sectionId}`);
      return section ? section.querySelector('reorder-notice') : null;
    };
    document.addEventListener('shopify:section:select', (event) => {
      selectedSection = event.detail.sectionId;
      const notice = noticeIn(selectedSection);
      if (notice && notice.show) notice.show();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      const notice = noticeIn(event.detail.sectionId);
      if (notice && notice.hide) notice.hide();
      if (selectedSection === event.detail.sectionId) selectedSection = null;
    });
  }
})();
